import { useState, useEffect } from 'react';
import {
  Button,
  InlineNotification,
  ModalWrapper,
  RadioTile,
  SkeletonPlaceholder,
  TextInput,
  TileGroup,
  Tooltip,
} from 'carbon-components-react';

import Cookies from 'js-cookie';
import Helmet from 'react-helmet';
import './COSPriceEstimator.scss';
import 'src/common/style/importantStyle.scss';
import 'src/common/style/common.scss';
import { segmentsTracking } from '../lib/tracker';
import Footnote from './Footnote';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import { useEnv } from 'src/common/hooks/widgets/useEnv';

import {
  LangDefaults,
  determineCountry,
  useLang,
} from 'src/common/hooks/widgets/useLang';
import { STORAGE_BUCKET, USAGE_BACKUP_STORAGE } from '../constants/price';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import * as URX_FORM from '../constants/urx-form';
import { IBMLocale } from 'src/common/locale/mapValidLocale';

import countryList from '../services/currencies.json';
import messages from '../locales/messages';
import Thanks from './Thanks';
import { FormModal } from 'src/common/utils/FormModal';
import {
  urxScript,
  urxScriptLoader,
} from 'src/common/hooks/widgets/globalDefinitions';
import { FormattedMessage } from 'react-intl';
import { ArrowRight16 } from '@carbon/icons-react';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import { pageTitle, REGEX } from '../constants/default';
import {
  calculateClassAFromUsage,
  calculateClassBFromUsage,
  calculateRetrievalFromUsage,
  convertGBToTB,
  convertInMillion,
} from '../lib/util';
import { usePrice } from '../hooks/usePrice';
import COSDisclaimerText from './COSDisclaimerText';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';

export interface COSPriceEstimatorProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
}

const COSPriceEstimator = ({
  langCode,
  test,
  countryToTest,
}: COSPriceEstimatorProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [currency, setCurrency] = useState('');
  const [country, setCountry] = useState('');

  // lang and userlocale is based out of ddo
  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
  );
  const [drawerOpen, setDrawerOpen] = useState(false);

  /* Have urx form deault in us-en when on language us page */
  const [urxlang] = useState(localeCode.toString());
  const [localCurrency, setlocalCurrency] = useState<string>(localeCode);

  const [toggleCountry, setToggleCountry] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  const [modalLocalCurrency, setModalLocalCurrency] = useState('USD');
  const [modalLocalLanguage, setModalLocalLanguage] = useState('English');

  useEffect(() => {
    setlocalCurrency(userLocale); // show local Currency
  }, [userLocale, localeCode, localCurrency]);

  const [localExist, setLocalExist] = useState(false);
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound === null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);

  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    countryList,
    true,
    test,
    localeCode,
  );

  const [environment] = useEnv();
  const [formComplete, setFormComplete] = useState(false);
  const [bpConsentMail, setBpConsentMail] = useState(false);
  const cookieURL = useCookiesURL();
  const isSmall = useSmallBreakpoint('560px');

  // COS for states
  const [usage, setUsage] = useState(USAGE_BACKUP_STORAGE.small_10_TB);
  const [retrieval, setRetrieval] = useState(
    calculateRetrievalFromUsage(usage),
  );
  const [classA, setClassA] = useState(calculateClassAFromUsage(usage));
  const [classB, setClassB] = useState(calculateClassBFromUsage(usage));

  const [storageBucket] = useState(STORAGE_BUCKET.coldVault);
  const [maxStorage, setMaxStorage] = useState(false);
  const [customUsage, setCustomUsage] = useState(convertGBToTB(usage));
  const [usageRadio, setUsageRadio] = useState(false);

  const [totalCost, totalMthlyCost] = usePrice({
    currency,
    localeCode,
    country,
    usage,
    retrieval,
    classA,
    classB,
  });

  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
    setDrawerOpen(false);
  };

  useEffect(() => {
    setCookie();
  }, [usage, retrieval, classA, classB]);

  const calculateMetrices = (backupStorageInGB: number) => {
    setMaxStorage(false);
    setUsage(backupStorageInGB);
    setCustomUsage(convertGBToTB(backupStorageInGB));
    setRetrieval(calculateRetrievalFromUsage(backupStorageInGB));
    setClassA(calculateClassAFromUsage(backupStorageInGB));
    setClassB(calculateClassBFromUsage(backupStorageInGB));
  };

  const setCookie = () => {
    const mailCheckbox =
      document.querySelector<HTMLInputElement>('#Q_MAIL_CONSENT');
    setBpConsentMail(!!mailCheckbox?.checked); // set false if unchecked or if undefined

    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: URX_FORM.FORM_ID,
        // One field to provide all values (stringify)
        dcq: {
          Q_NOTESDATA:
            cookieURL +
            ' | Usage Backup Storage:' +
            convertGBToTB(usage) +
            ' | Retrieval Backup Restore:' +
            retrieval +
            ' | Class A (Puts): ' +
            classA +
            ' | Class B (Gets): ' +
            classB +
            ` | BP Mail consent:${bpConsentMail}` +
            ' | Estimator URL:' +
            window.location.href, // BP mail consent checkbox in form
          Q_ASSET_URL: window.location.href, // ISC - Asset URL initial
          Q_ASSET_NAME: 'Cloud Object Storage Price Estimator', // ISC - Asset Name
        },
      }),
    );
    console.log(Cookies.get('urxdcq'));
  };

  return (
    <div className="cos-estimator widget-styles">
      <Helmet>
        <script src={urxScript}></script>
        <script src={urxScriptLoader}></script>
      </Helmet>
      {formComplete ? (
        <Thanks
          bpName={''}
          bpConsentMail={bpConsentMail}
          totalYrlyPrice={''}
          totalMthlyPrice={''}
          backupStorage={''}
          storageBucket={''}
          backupRestore={''}
          writeRequestClassA={''}
          readRequestClassB={''}
        />
      ) : (
        <div id="COSGrid">
          <div className="bx--row mt-5">
            <div
              className="bx--col-lg-4 bx--col-md-8 bx--col-sm-8 bx--offset-lg-12"
              id="countryModal"
            >
              {false && localExist && toggleCountry ? (
                <ModalWrapper
                  modalLabel={`Found country ${modalLocalCountry}. Access page in ${modalLocalLanguage} (${modalLocalCurrency}) `}
                  modalHeading={`The COS estimator is available in ${modalLocalLanguage} with local currency ${modalLocalCurrency}`}
                  size={'sm'}
                  primaryButtonText={'Cool! Take me there'}
                  secondaryButtonText={''}
                  buttonTriggerText={`Access page in ${modalLocalLanguage}`}
                  handleOpen={() => {
                    segmentsTracking('CTA Clicked', {
                      pageTitle: 'COS Landing Page',
                      object: 'Pricing page',
                      resultValue: 'CTA clicked',
                      CTA: 'SIEM (SaaS) tile',
                      location: 'Estimator',
                      action: '',
                      field: '',
                    });
                  }}
                  handleSubmit={() => {
                    localStorage.setItem('langLocale', selectedLocale);
                    (window.location.href = window.location.href =
                      'https://www.ibm.com/' +
                      selectedLocale +
                      '/products/cloud-object-storage/pricing'),
                      segmentsTracking('CTA Clicked', {
                        pageTitle: 'COS Landing Page',
                        object: 'Pricing page',
                        resultValue: 'CTA clicked',
                        CTA: 'SIEM (SaaS) tile',
                        location: 'Estimator',
                        action: '',
                        field: '',
                      });
                    return true;
                  }}
                ></ModalWrapper>
              ) : null}
            </div>
          </div>
          <div className="bx--row mar3">
            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
              <div className="bx--row">
                <div className="bx--col-lg-9 bx--col-md-16 bx--col-sm-16">
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <p className="page_heading_3">
                        <FormattedMessage {...messages.main_heading} />
                      </p>
                    </div>
                  </div>
                  <div className="bx--row mar3">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <p className="page_sub_heading">
                        <FormattedMessage {...messages.sub_heading} />
                      </p>
                    </div>
                  </div>
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <InlineNotification
                        className="sm-md-align"
                        kind="info"
                        role="cos"
                        title={
                          <div className="cos-inline-heading">
                            <FormattedMessage {...messages.inline_heading} />
                          </div>
                        }
                        subtitle={
                          <div className="cos-inline-body">
                            <FormattedMessage
                              {...messages.inline_sub_heading}
                            />
                            <ul>
                              <li>
                                <FormattedMessage
                                  {...messages.inline_sub_option_1}
                                />
                              </li>
                              <li>
                                <FormattedMessage
                                  {...messages.inline_sub_option_2}
                                />
                              </li>
                            </ul>
                          </div>
                        }
                        hideCloseButton={true}
                      />
                    </div>
                  </div>
                  <div className="bx--row mt-5">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <TileGroup
                        name="usage-backup-storage"
                        className="cos-estimator__tilegroup"
                        valueSelected={USAGE_BACKUP_STORAGE.small_10_TB}
                      >
                        <RadioTile
                          id="usage-storage-small"
                          tabIndex={1}
                          value={USAGE_BACKUP_STORAGE.small_10_TB}
                          className="cos-estimator__tile"
                          disabled={usageRadio}
                          onClick={() => {
                            segmentsTracking('CTA Clicked', {
                              pageTitle: 'COS PE',
                              object: 'COS page',
                              resultValue: 'CTA clicked',
                              CTA: `Usage for backup storage - small 10 TB - selected`,
                              location: 'Pricing',
                              action: '',
                              field: '',
                            });
                            calculateMetrices(USAGE_BACKUP_STORAGE.small_10_TB);
                          }}
                        >
                          <p className="cos-radio-title">
                            <FormattedMessage {...messages.small_10_tb} />
                          </p>
                        </RadioTile>
                        <RadioTile
                          id="usage-storage-medium"
                          tabIndex={2}
                          value={USAGE_BACKUP_STORAGE.medium_100_TB}
                          className="cos-estimator__tile"
                          disabled={usageRadio}
                          onClick={() => {
                            segmentsTracking('CTA Clicked', {
                              pageTitle: 'COS PE',
                              object: 'COS page',
                              resultValue: 'CTA clicked',
                              CTA: `Usage for backup storage - medium 100 TB - selected`,
                              location: 'Pricing',
                              action: '',
                              field: '',
                            });
                            calculateMetrices(
                              USAGE_BACKUP_STORAGE.medium_100_TB,
                            );
                          }}
                        >
                          <p className="cos-radio-title">
                            <FormattedMessage {...messages.medium_100_tb} />
                          </p>
                        </RadioTile>
                        <RadioTile
                          id="usage-storage-large"
                          tabIndex={3}
                          value={USAGE_BACKUP_STORAGE.large_500_TB}
                          className="cos-estimator__tile"
                          disabled={usageRadio}
                          onClick={() => {
                            segmentsTracking('CTA Clicked', {
                              pageTitle: 'COS PE',
                              object: 'COS page',
                              resultValue: 'CTA clicked',
                              CTA: `Usage for backup storage - large 500 TB - selected`,
                              location: 'Pricing',
                              action: '',
                              field: '',
                            });
                            calculateMetrices(
                              USAGE_BACKUP_STORAGE.large_500_TB,
                            );
                          }}
                        >
                          <p className="cos-radio-title">
                            <FormattedMessage {...messages.large_500_tb} />
                          </p>
                        </RadioTile>
                      </TileGroup>
                    </div>
                  </div>
                  <div className="bx--row mt-5">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <p className="page_body_2">
                        <FormattedMessage {...messages.custom_backup_storage} />
                      </p>
                    </div>
                  </div>
                  <div className="bx--row">
                    <div className="bx--col-lg-8 bx--col-md-8 bx--col-sm-4">
                      <TextInput
                        className="bx--text-input bx--text-input--lg"
                        id="usage-storage-custom"
                        invalidText={
                          <FormattedMessage
                            {...messages.custom_backup_storage_error_message}
                          />
                        }
                        hideLabel={true}
                        value={customUsage}
                        invalid={maxStorage}
                        onBlur={() => setUsageRadio(false)}
                        onFocus={() => setUsageRadio(true)}
                        onChange={(e) => {
                          const element = e.currentTarget as HTMLInputElement;
                          const value = element.value.replaceAll(',', '');
                          const invalid =
                            !REGEX.test(value) && value.length > 0;
                          const inputUsageInTB = parseInt(value);

                          // For segment tracking
                          segmentsTracking('CTA Clicked', {
                            pageTitle: 'COS PE',
                            object: 'COS page',
                            resultValue: 'CTA clicked',
                            CTA: `Usage for backup storage - custom storage ${inputUsageInTB} TB - entered`,
                            location: 'Pricing',
                            action: '',
                            field: '',
                          });

                          if (invalid || Number.isNaN(parseInt(value))) {
                            setCustomUsage(0);
                          } else {
                            if (inputUsageInTB > 500) {
                              setMaxStorage(true);
                              setCustomUsage(inputUsageInTB);
                            } else {
                              setMaxStorage(false);
                              setCustomUsage(inputUsageInTB);
                              calculateMetrices(inputUsageInTB * 1000);
                            }
                          }
                        }}
                        labelText={
                          <FormattedMessage
                            {...messages.custom_backup_storage}
                          />
                        }
                      />
                    </div>
                  </div>
                  {/* Legal Disclaimer */}
                  <div id="footnote" className="div-only-lg mar3">
                    <div className="bx--row">
                      <div className="bx--col-lg-16">
                        <div id="footnote">
                          <Footnote numNote={1} />
                          <div className="footnote_text">
                            <COSDisclaimerText localeCode="us-en" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bx--col-lg-1"></div>
                {/* Pricing Table */}
                <div className="bx--col-lg-5 bx--col-md-16 bx--col-sm-16 pricing-summary">
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <div className="bx--row">
                        <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                          <p className="page_body">
                            <FormattedMessage {...messages.pricing_estimated} />
                          </p>
                        </div>
                        <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4 mar7">
                          {Number(totalCost) > 0 ? (
                            <p className="cos_price">
                              {totalMthlyCost}
                              <sup>*</sup>
                            </p>
                          ) : (
                            <SkeletonPlaceholder className="cos-estimator__sticky-nav-skeleton" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bx--row mar7">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <p className="page_body_2 bold">
                        <FormattedMessage {...messages.pricing_configuration} />
                      </p>
                    </div>
                  </div>
                  <hr className="cos-estimator__custom-hr" />
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <div className="bx--row">
                        <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-2 cos-flex-grid">
                          <div className="bx--row">
                            <div className="bx--col-lg-12 bx--col-md-16 bx--col-sm-2">
                              <p className="cos-pricing-table">
                                <FormattedMessage
                                  {...messages.pricing_backup_storage}
                                />
                              </p>
                            </div>
                            <div className="bx--col-lg-4 bx--col-md-16 bx--col-sm-2">
                              <Tooltip
                                onChange={() => {
                                  // For segment tracking
                                  segmentsTracking('CTA Clicked', {
                                    pageTitle: 'COS PE',
                                    object: 'COS page',
                                    resultValue: 'CTA clicked',
                                    CTA: `Tooltip - backup storage`,
                                    location: 'Pricing',
                                    action: '',
                                    field: '',
                                  });
                                }}
                                direction={isSmall ? 'bottom' : 'right'}
                                tabIndex={0}
                              >
                                <span className="cos-estimator__cos-tooltip-text slide_text">
                                  <FormattedMessage
                                    {...messages.pricing_backup_storage_tooltip}
                                  />
                                </span>
                              </Tooltip>
                            </div>
                          </div>
                        </div>
                        <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-2">
                          <p className="cos-pricing-table">{`${convertGBToTB(
                            usage,
                          )} TB`}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr className="cos-estimator__custom-hr" />
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <div className="bx--row">
                        <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-2 cos-flex-grid">
                          <div className="bx--row">
                            <div className="bx--col-lg-12 bx--col-md-16 bx--col-sm-2">
                              <p className="cos-pricing-table">
                                <FormattedMessage
                                  {...messages.pricing_storage_bucket}
                                />
                              </p>
                            </div>
                            <div className="bx--col-lg-4 bx--col-md-16 bx--col-sm-2">
                              <Tooltip
                                onChange={() => {
                                  // For segment tracking
                                  segmentsTracking('CTA Clicked', {
                                    pageTitle: 'COS PE',
                                    object: 'COS page',
                                    resultValue: 'CTA clicked',
                                    CTA: `Tooltip - storage bucket`,
                                    location: 'Pricing',
                                    action: '',
                                    field: '',
                                  });
                                }}
                                direction={isSmall ? 'bottom' : 'right'}
                                tabIndex={0}
                              >
                                <span className="cos-estimator__cos-tooltip-text slide_text">
                                  <FormattedMessage
                                    {...messages.pricing_storage_bucket_tooltip}
                                  />
                                </span>
                              </Tooltip>
                            </div>
                          </div>
                        </div>
                        <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-2">
                          <p className="cos-pricing-table">{storageBucket}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr className="cos-estimator__custom-hr" />
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <div className="bx--row">
                        <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-2 cos-flex-grid">
                          <div className="bx--row">
                            <div className="bx--col-lg-12 bx--col-md-16 bx--col-sm-2">
                              <p className="cos-pricing-table-2">
                                <FormattedMessage
                                  {...messages.pricing_backup_restore}
                                />
                              </p>
                            </div>
                            <div className="bx--col-lg-4 bx--col-md-16 bx--col-sm-2">
                              <Tooltip
                                onChange={() => {
                                  // For segment tracking
                                  segmentsTracking('CTA Clicked', {
                                    pageTitle: 'COS PE',
                                    object: 'COS page',
                                    resultValue: 'CTA clicked',
                                    CTA: `Tooltip - backup restore`,
                                    location: 'Pricing',
                                    action: '',
                                    field: '',
                                  });
                                }}
                                direction={isSmall ? 'bottom' : 'right'}
                                tabIndex={0}
                              >
                                <span className="cos-estimator__cos-tooltip-text slide_text">
                                  <FormattedMessage
                                    {...messages.pricing_backup_restore_tooltip}
                                  />
                                </span>
                              </Tooltip>
                            </div>
                          </div>
                        </div>
                        <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-2">
                          <p className="cos-pricing-table-2">
                            {retrieval > 1000
                              ? `${convertGBToTB(retrieval)} TB`
                              : `${retrieval} GB`}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <div className="bx--row">
                        <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-2 cos-flex-grid">
                          <div className="bx--row">
                            <div className="bx--col-lg-12 bx--col-md-16 bx--col-sm-2">
                              <p className="cos-pricing-table-2">
                                <FormattedMessage
                                  {...messages.pricing_write_requests}
                                />
                              </p>
                            </div>
                            <div className="bx--col-lg-4 bx--col-md-16 bx--col-sm-2">
                              <Tooltip
                                onChange={() => {
                                  // For segment tracking
                                  segmentsTracking('CTA Clicked', {
                                    pageTitle: 'COS PE',
                                    object: 'COS page',
                                    resultValue: 'CTA clicked',
                                    CTA: `Tooltip - wrting requests`,
                                    location: 'Pricing',
                                    action: '',
                                    field: '',
                                  });
                                }}
                                direction={isSmall ? 'bottom' : 'right'}
                                tabIndex={0}
                              >
                                <span className="cos-estimator__cos-tooltip-text slide_text">
                                  <FormattedMessage
                                    {...messages.pricing_write_requests_tooltip}
                                  />
                                </span>
                              </Tooltip>
                            </div>
                          </div>
                        </div>
                        <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-2">
                          <p className="cos-pricing-table-2">{`${convertInMillion(
                            classA,
                          )} Million Ops`}</p>
                        </div>
                      </div>
                      <div className="bx--row">
                        <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-2 cos-flex-grid">
                          <div className="bx--row">
                            <div className="bx--col-lg-12 bx--col-md-16 bx--col-sm-2">
                              <p className="cos-pricing-table-2">
                                <FormattedMessage
                                  {...messages.pricing_read_requests}
                                />
                              </p>
                            </div>
                            <div className="bx--col-lg-4 bx--col-md-16 bx--col-sm-2">
                              <Tooltip
                                onChange={() => {
                                  // For segment tracking
                                  segmentsTracking('CTA Clicked', {
                                    pageTitle: 'COS PE',
                                    object: 'COS page',
                                    resultValue: 'CTA clicked',
                                    CTA: `Tooltip - read requests`,
                                    location: 'Pricing',
                                    action: '',
                                    field: '',
                                  });
                                }}
                                direction={isSmall ? 'bottom' : 'right'}
                                tabIndex={0}
                              >
                                <span className="cos-estimator__cos-tooltip-text slide_text">
                                  <FormattedMessage
                                    {...messages.pricing_read_requests_tooltip}
                                  />
                                </span>
                              </Tooltip>
                            </div>
                          </div>
                        </div>
                        <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-2">
                          <p className="cos-pricing-table-2">{`${convertInMillion(
                            classB,
                          )} Million Ops`}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <p className="price_table_comp__disclaimer price_table_comp_color padding_24_1">
                        * <FormattedMessage {...messages.pricing_disclaimer} />
                      </p>
                    </div>
                  </div>
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <div className="bx--row">
                        <div className="bx--col-lg-8 bx--col-md-4 bx--col-sm-2">
                          <Button
                            className="buy-now"
                            kind="primary"
                            renderIcon={ArrowRight16}
                            id="request_btn"
                            onClick={() => {
                              segmentsTracking('CTA Clicked', {
                                pageTitle: pageTitle,
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'buy now button',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                              setCookie();
                              setTimeout(
                                () =>
                                  window.open(
                                    'https://cloud.ibm.com/objectstorage/create',
                                    '_target',
                                  ),
                                250,
                              );
                            }}
                          >
                            <FormattedMessage {...messages.pricing_buy_now} />
                          </Button>
                        </div>
                        <div className="bx--col-lg-8 bx--col-md-4 bx--col-sm-2">
                          <Button
                            className="buy-now"
                            kind="tertiary"
                            renderIcon={ArrowRight16}
                            onClick={() => {
                              segmentsTracking('CTA Clicked', {
                                pageTitle: pageTitle,
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'request a quote',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                              setCookie();
                              initUrxForm(
                                environment,
                                URX_FORM.INSTANCE_ID,
                                URX_FORM.FORM_ID,
                                urxlang as IBMLocale,
                              );
                              setDrawerOpen(true);
                            }}
                          >
                            <FormattedMessage {...messages.request_a_quote} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div id="footnote" className="div-only-media">
                  <div className="bx--row">
                    <div className="bx--col-lg-16 marginleft_1">
                      <div>
                        <Footnote numNote={1} />
                      </div>
                      <div className="footnote_text">
                        <COSDisclaimerText localeCode="us-en" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <FormModal
            pageTitle="COS Page"
            object="COS Page"
            productTitle={'COS'}
            pageUrl={''}
            location={'Estimator'}
            bpName={''}
            logo={''}
            drawerOpen={drawerOpen}
            setDrawerOpen={setDrawerOpen}
            formId={URX_FORM.FORM_ID}
            lang={lang}
          ></FormModal>
        </div>
      )}
    </div>
  );
};

export default COSPriceEstimator;
